/**
 * Commission Intelligence Dashboard
 * Tabs: Pending Approvals | History | Customer Ownership | Employee Rates
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../services/api";

const C = {
  bg:     "#060e20",
  card:   "rgba(255,255,255,0.02)",
  border: "rgba(189,178,255,0.15)",
  purple: "#bdb2ff",
  green:  "#cfeda2",
  peach:  "#ffd6a5",
  red:    "#ff6e84",
  text:   "#dde5ff",
  muted:  "#6d758b",
  blur:   "blur(60px)",
};

const glass: React.CSSProperties = {
  background: C.card,
  backdropFilter: C.blur,
  WebkitBackdropFilter: C.blur,
  border: `0.5px solid ${C.border}`,
  borderRadius: 16,
};

interface InvoiceDoc {
  filename: string;
  path?: string;
  invoice_type: string;
  ocr_vendor?: string;
  ocr_amount_aed?: number;
  expected_amount?: number;
  matched?: boolean;
  uploaded_at?: string;
}

interface CommissionEvent {
  id: string;
  lead_name: string;
  employee_name: string;
  manager_name: string;
  sale_amount: number;
  procurement_cost: number;
  handling_cost: number;
  profit: number;
  employee_rate: number;
  employee_commission: number;
  manager_rate: number;
  manager_commission: number;
  evidence_status: string;
  evidence_notes: string;
  status: string;
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  created_at: string;
  documents_attached?: Record<string, InvoiceDoc>;
}

interface CustomerOwnership {
  id: string;
  customer_phone: string;
  customer_name: string;
  employee_name: string;
  created_at: string;
  reassigned_by?: string;
}

interface EmployeeRate {
  employee_name: string;
  rate: number;
  rate_pct: string;
  is_active: boolean;
  updated_by?: string;
}

const fmt = (n: number) =>
  `AED ${(n || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      letterSpacing: 0.5, textTransform: "uppercase" as const,
      background: `${color}22`, color, border: `1px solid ${color}55`,
    }}>{label.replace("_", " ")}</span>
  );
}

const EVIDENCE_COLORS: Record<string, string> = {
  verified: "#cfeda2", partial: "#ffd6a5", needs_review: "#bdb2ff", rejected: "#ff6e84",
};
const STATUS_COLORS: Record<string, string> = {
  pending: "#ffd6a5", approved: "#cfeda2", rejected: "#ff6e84", paid: "#bdb2ff",
};

const INVOICE_KINDS: { key: "sales" | "procurement" | "handling"; label: string; icon: string }[] = [
  { key: "sales",       label: "Sales Invoice",       icon: "📄" },
  { key: "procurement", label: "Procurement Invoice", icon: "📄" },
  { key: "handling",    label: "Handling Receipt",    icon: "📄" },
];

function InvoiceUploadZone({
  event,
  kind,
  doc,
  onUploaded,
}: {
  event: CommissionEvent;
  kind: { key: "sales" | "procurement" | "handling"; label: string; icon: string };
  doc?: InvoiceDoc;
  onUploaded: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(f: File | null) {
    if (!f) return;
    setErr(null);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("invoice_type", kind.key);
      await api.commission.uploadInvoice(event.id, form);
      onUploaded();
    } catch (e: any) {
      setErr(e?.message || "Upload failed");
    }
    setBusy(false);
  }

  const mismatched = doc && doc.matched === false;
  const verified   = doc && doc.matched === true;
  const borderColor = verified ? C.green : mismatched ? C.peach : C.purple;
  const bg = verified
    ? "rgba(207,237,162,0.08)"
    : mismatched
    ? "rgba(255,214,165,0.08)"
    : "rgba(189,178,255,0.04)";

  return (
    <div
      onClick={() => !busy && inputRef.current?.click()}
      style={{
        flex: "1 1 180px",
        minWidth: 180,
        border: verified || mismatched ? `1px solid ${borderColor}` : `1.5px dashed ${borderColor}`,
        background: bg,
        borderRadius: 10,
        padding: "0.75rem 1rem",
        cursor: busy ? "wait" : "pointer",
        color: C.text,
        fontSize: 12,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={e => handleFile(e.target.files?.[0] ?? null)}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700 }}>
        <span>{kind.icon}</span>
        <span style={{ color: borderColor }}>
          {verified ? "✅" : mismatched ? "⚠️" : ""} {kind.label}
        </span>
      </div>
      {busy && <div style={{ marginTop: 6, color: C.muted }}>Uploading & OCR…</div>}
      {!busy && !doc && (
        <div style={{ marginTop: 6, color: C.muted }}>Click to upload (PDF / JPG / PNG)</div>
      )}
      {!busy && doc && (
        <div style={{ marginTop: 6, color: C.muted, lineHeight: 1.5 }}>
          <div style={{ color: C.text, fontWeight: 600, wordBreak: "break-all" }}>{doc.filename}</div>
          <div>OCR: {fmt(doc.ocr_amount_aed || 0)}</div>
          {mismatched && (
            <div style={{ color: C.peach, marginTop: 2 }}>
              Amount mismatch — review needed ({fmt(doc.expected_amount || 0)} expected)
            </div>
          )}
        </div>
      )}
      {err && <div style={{ marginTop: 6, color: C.red }}>{err}</div>}
    </div>
  );
}

function EventCard({ event, onAction }: { event: CommissionEvent; onAction: () => void }) {
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function approve() {
    setLoading(true);
    setActionError(null);
    try { 
      await api.commission.approve(event.id); 
      onAction();
    } catch (e: any) {
      setActionError(e?.message || "Approval failed");
    }
    setLoading(false);
  }

  async function reject() {
    if (!rejectReason.trim()) return;
    setLoading(true);
    setActionError(null);
    try { 
      await api.commission.reject(event.id, rejectReason); 
      setShowReject(false);
      setRejectReason("");
      onAction();
    } catch (e: any) {
      setActionError(e?.message || "Rejection failed");
    }
    setLoading(false);
  }

  return (
    <div style={{ ...glass, padding: "1.5rem", marginBottom: "1rem" }}>
      {actionError && (
        <div style={{ 
          background: "rgba(255,110,132,0.1)", 
          border: `1px solid ${C.red}`, 
          borderRadius: 8, 
          padding: "0.5rem 1rem", 
          marginBottom: "1rem",
          color: C.red,
          fontSize: 12,
        }}>
          {actionError}
        </div>
      )}
      
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 4 }}>{event.lead_name}</div>
          <div style={{ fontSize: 13, color: C.muted }}>
            Salesperson: <span style={{ color: C.purple, fontWeight: 600 }}>{event.employee_name}</span>
            &nbsp;· Manager: <span style={{ color: C.peach, fontWeight: 600 }}>{event.manager_name}</span>
          </div>
          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Badge label={event.status} color={STATUS_COLORS[event.status] || C.muted} />
            <Badge label={event.evidence_status} color={EVIDENCE_COLORS[event.evidence_status] || C.muted} />
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 13, color: C.muted }}>Profit</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: event.profit >= 0 ? C.green : C.red }}>
            {fmt(event.profit)}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginTop: 16 }}>
        {[
          ["Sale", fmt(event.sale_amount), C.text],
          ["Procurement", fmt(event.procurement_cost), C.muted],
          ["Handling", fmt(event.handling_cost), C.muted],
          [`Employee (${((event.employee_rate || 0.1) * 100).toFixed(0)}%)`, fmt(event.employee_commission), C.purple],
          ["Manager (20%)", fmt(event.manager_commission), C.peach],
        ].map(([l, v, c]) => (
          <div key={l as string} style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, padding: "0.75rem", border: `0.5px solid ${C.border}` }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c as string }}>{v}</div>
          </div>
        ))}
      </div>

      {event.evidence_notes && (
        <div style={{ marginTop: 12, padding: "0.75rem 1rem", background: "rgba(189,178,255,0.06)", borderRadius: 8, borderLeft: `3px solid ${C.purple}`, fontSize: 12, color: C.muted }}>
          {event.evidence_notes}
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {INVOICE_KINDS.map(k => (
          <InvoiceUploadZone
            key={k.key}
            event={event}
            kind={k}
            doc={event.documents_attached?.[k.key]}
            onUploaded={onAction}
          />
        ))}
      </div>

      {event.status === "pending" && (
        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={approve} disabled={loading}
            style={{ padding: "0.6rem 1.4rem", background: `linear-gradient(135deg,${C.green},${C.purple})`, border: "none", borderRadius: 8, color: "#060e20", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
            {loading ? "Processing..." : "✓ Approve"}
          </button>
          <button onClick={() => setShowReject(!showReject)} disabled={loading}
            style={{ padding: "0.6rem 1.4rem", background: "rgba(255,110,132,0.15)", border: `1px solid ${C.red}`, borderRadius: 8, color: C.red, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
            ✕ Reject
          </button>
        </div>
      )}

      {showReject && (
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="Rejection reason..."
            style={{ flex: 1, padding: "0.6rem 1rem", background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13 }} />
          <button onClick={reject} disabled={loading || !rejectReason.trim()}
            style={{ padding: "0.6rem 1rem", background: "rgba(255,110,132,0.2)", border: `1px solid ${C.red}`, borderRadius: 8, color: C.red, fontWeight: 700, cursor: "pointer" }}>
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}

function ReassignModal({ 
  ownership, 
  onClose, 
  onReassigned 
}: { 
  ownership: CustomerOwnership; 
  onClose: () => void; 
  onReassigned: () => void;
}) {
  const [newEmployee, setNewEmployee] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleReassign() {
    if (!newEmployee.trim() || !reason.trim()) return;
    
    setLoading(true);
    setError(null);
    try {
      await api.commission.reassignCustomer({
        customer_phone: ownership.customer_phone,
        new_employee: newEmployee,
        admin_name: "Current User", // Should come from auth context
        reason: reason,
      });
      onReassigned();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Reassignment failed");
    }
    setLoading(false);
  }

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    }}>
      <div style={{
        ...glass,
        padding: "2rem",
        width: "90%",
        maxWidth: 500,
        color: C.text,
      }}>
        <h3 style={{ marginTop: 0, color: C.purple }}>Reassign Customer</h3>
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Customer</div>
          <div style={{ fontWeight: 700 }}>{ownership.customer_name || "Unknown"}</div>
          <div style={{ color: C.muted, fontSize: 12 }}>{ownership.customer_phone}</div>
        </div>
        
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Current Employee</div>
          <div style={{ fontWeight: 700, color: C.purple }}>{ownership.employee_name}</div>
        </div>
        
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", color: C.muted, fontSize: 12, marginBottom: 4 }}>
            New Employee *
          </label>
          <input
            type="text"
            value={newEmployee}
            onChange={e => setNewEmployee(e.target.value)}
            placeholder="Enter employee name"
            style={{
              width: "100%",
              padding: "0.6rem 1rem",
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.text,
              fontSize: 13,
            }}
          />
        </div>
        
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ display: "block", color: C.muted, fontSize: 12, marginBottom: 4 }}>
            Reason for Reassignment *
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Explain why this customer is being reassigned..."
            rows={3}
            style={{
              width: "100%",
              padding: "0.6rem 1rem",
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.text,
              fontSize: 13,
              resize: "vertical",
            }}
          />
        </div>
        
        {error && (
          <div style={{
            background: "rgba(255,110,132,0.1)",
            border: `1px solid ${C.red}`,
            borderRadius: 8,
            padding: "0.5rem 1rem",
            marginBottom: "1rem",
            color: C.red,
            fontSize: 12,
          }}>
            {error}
          </div>
        )}
        
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "0.6rem 1.4rem",
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.muted,
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleReassign}
            disabled={loading || !newEmployee.trim() || !reason.trim()}
            style={{
              padding: "0.6rem 1.4rem",
              background: `linear-gradient(135deg,${C.green},${C.purple})`,
              border: "none",
              borderRadius: 8,
              color: "#060e20",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {loading ? "Reassigning..." : "Reassign Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CommissionDashboard() {
  const [tab, setTab] = useState<"pending"|"history"|"ownership"|"rates">("pending");
  const [events, setEvents] = useState<CommissionEvent[]>([]);
  const [ownership, setOwnership] = useState<CustomerOwnership[]>([]);
  const [rates, setRates] = useState<EmployeeRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [rateEdit, setRateEdit] = useState<Record<string, string>>({});
  const [reassignTarget, setReassignTarget] = useState<CustomerOwnership | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = useCallback(async (status?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.commission.events(status);
      setEvents(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load events");
      setEvents([]);
    }
    setLoading(false);
  }, []);

  const loadOwnership = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.commission.ownership();
      setOwnership(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load ownership records");
      setOwnership([]);
    }
    setLoading(false);
  }, []);

  const loadRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.commission.rates();
      setRates(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load commission rates");
      setRates([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === "pending")   loadEvents("pending");
    if (tab === "history")   loadEvents();
    if (tab === "ownership") loadOwnership();
    if (tab === "rates")     loadRates();
  }, [tab, loadEvents, loadOwnership, loadRates]);

  async function saveRate(employee: string) {
    const val = parseFloat(rateEdit[employee] || "");
    if (isNaN(val) || val < 0 || val > 100) {
      setError("Rate must be between 0 and 100%");
      return;
    }
    
    try { 
      await api.commission.setRate({ employee_name: employee, rate: val / 100 }); 
      const updated = { ...rateEdit };
      delete updated[employee];
      setRateEdit(updated);
      loadRates();
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to save rate");
    }
  }

  const pendingCount = events.filter(e => e.status === "pending").length;
  const TABS = [
    { key: "pending" as const,   label: "Pending Approvals" },
    { key: "history" as const,   label: "History" },
    { key: "ownership" as const, label: "Customer Ownership" },
    { key: "rates" as const,     label: "Employee Rates" },
  ];

  return (
    <div style={{ padding: "2rem", maxWidth: 1200, margin: "0 auto", fontFamily: "'Space Grotesk',sans-serif", color: C.text }}>
      {error && (
        <div style={{
          background: "rgba(255,110,132,0.1)",
          border: `1px solid ${C.red}`,
          borderRadius: 12,
          padding: "1rem 1.5rem",
          marginBottom: "1.5rem",
          color: C.red,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <div>{error}</div>
          <button 
            onClick={() => setError(null)}
            style={{
              background: "none",
              border: "none",
              color: C.red,
              cursor: "pointer",
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>
      )}
      
      <div style={{ ...glass, padding: "1.5rem 2rem", marginBottom: "2rem", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 32 }}>💰</div>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, background: `linear-gradient(135deg,${C.green},${C.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
            Commission Intelligence
          </h1>
          <div style={{ color: C.muted, fontSize: 13 }}>Profit-based sales commission · AFAQ ALNASEEM</div>
        </div>
        {pendingCount > 0 && (
          <div style={{ marginLeft: "auto", background: `${C.peach}22`, border: `1px solid ${C.peach}55`, borderRadius: 20, padding: "4px 14px", color: C.peach, fontSize: 13, fontWeight: 700 }}>
            {pendingCount} pending
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem", flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "0.6rem 1.4rem", borderRadius: 10, fontFamily: "'Space Grotesk',sans-serif",
            fontWeight: 700, fontSize: 13, cursor: "pointer",
            background: tab === t.key ? `linear-gradient(135deg,${C.green}33,${C.purple}33)` : "rgba(255,255,255,0.03)",
            border: tab === t.key ? `1px solid ${C.purple}` : `0.5px solid ${C.border}`,
            color: tab === t.key ? C.text : C.muted,
          }}>
            {t.label}
            {t.key === "pending" && pendingCount > 0 && (
              <span style={{ marginLeft: 6, background: C.peach, color: "#060e20", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", padding: "3rem", color: C.muted }}>Loading...</div>}

      {!loading && (tab === "pending" || tab === "history") && (
        <div>
          {events.length === 0
            ? <div style={{ ...glass, padding: "3rem", textAlign: "center", color: C.muted }}>{tab === "pending" ? "No pending commissions." : "No commission history yet."}</div>
            : events.map(e => <EventCard key={e.id} event={e} onAction={() => tab === "pending" ? loadEvents("pending") : loadEvents()} />)
          }
        </div>
      )}

      {!loading && tab === "ownership" && (
        <div style={{ ...glass, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "rgba(189,178,255,0.08)", borderBottom: `1px solid ${C.border}` }}>
                {["Customer", "Phone", "Assigned To", "Since", "Reassigned By", ""].map(h => (
                  <th key={h} style={{ padding: "0.75rem 1rem", textAlign: "left", color: C.muted, fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ownership.length === 0
                ? <tr><td colSpan={6} style={{ padding: "2rem", textAlign: "center", color: C.muted }}>No ownership records yet.</td></tr>
                : ownership.map(o => (
                    <tr key={o.id} style={{ borderBottom: `0.5px solid ${C.border}` }}>
                      <td style={{ padding: "0.75rem 1rem", color: C.text, fontWeight: 600 }}>{o.customer_name || "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: C.muted }}>{o.customer_phone}</td>
                      <td style={{ padding: "0.75rem 1rem", color: C.purple, fontWeight: 700 }}>{o.employee_name}</td>
                      <td style={{ padding: "0.75rem 1rem", color: C.muted }}>{o.created_at?.slice(0, 10)}</td>
                      <td style={{ padding: "0.75rem 1rem", color: C.muted }}>{o.reassigned_by || "—"}</td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <button
                          onClick={() => setReassignTarget(o)}
                          style={{
                            padding: "0.4rem 0.8rem",
                            background: "rgba(189,178,255,0.1)",
                            border: `1px solid ${C.purple}`,
                            borderRadius: 6,
                            color: C.purple,
                            cursor: "pointer",
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        >
                          Reassign
                        </button>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      )}

      {!loading && tab === "rates" && (
        <div>
          <div style={{ ...glass, overflow: "hidden", marginBottom: "1.5rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "rgba(189,178,255,0.08)", borderBottom: `1px solid ${C.border}` }}>
                  {["Employee", "Current Rate", "Override %", "Updated By", ""].map(h => (
                    <th key={h} style={{ padding: "0.75rem 1rem", textAlign: "left", color: C.muted, fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rates.length === 0
                  ? <tr><td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: C.muted }}>Default: 10% per employee. Process a sale to seed rates.</td></tr>
                  : rates.map(r => (
                      <tr key={r.employee_name} style={{ borderBottom: `0.5px solid ${C.border}` }}>
                        <td style={{ padding: "0.75rem 1rem", color: C.text, fontWeight: 700 }}>{r.employee_name}</td>
                        <td style={{ padding: "0.75rem 1rem", color: C.green, fontWeight: 700, fontSize: 15 }}>{r.rate_pct}</td>
                        <td style={{ padding: "0.75rem 1rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <input type="number" placeholder={String((r.rate * 100).toFixed(1))}
                              value={rateEdit[r.employee_name] || ""}
                              onChange={e => setRateEdit({ ...rateEdit, [r.employee_name]: e.target.value })}
                              style={{ width: 70, padding: "0.4rem 0.6rem", background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13 }}
                              min={0} max={100} step={0.5} />
                            <span style={{ color: C.muted }}>%</span>
                          </div>
                        </td>
                        <td style={{ padding: "0.75rem 1rem", color: C.muted }}>{r.updated_by || "—"}</td>
                        <td style={{ padding: "0.75rem 1rem" }}>
                          {rateEdit[r.employee_name] && (
                            <button onClick={() => saveRate(r.employee_name)}
                              style={{ padding: "0.4rem 1rem", background: `linear-gradient(135deg,${C.green}33,${C.purple}33)`, border: `1px solid ${C.purple}`, borderRadius: 6, color: C.text, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                              Save
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
          <div style={{ ...glass, padding: "1.25rem 1.5rem", fontSize: 12, color: C.muted }}>
            <strong style={{ color: C.purple }}>Formula: </strong>
            Profit = Sale - Procurement - Handling &nbsp;|&nbsp;
            Employee = Profit x rate &nbsp;|&nbsp;
            Manager = Profit x 20% (always independent)
          </div>
        </div>
      )}

      {reassignTarget && (
        <ReassignModal
          ownership={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onReassigned={() => {
            loadOwnership();
            setError(null);
          }}
        />
      )}
    </div>
  );
}